import { ANCHOR_ATTR, formatAnchors, MAX_OFF_FRAME_PCT, parseAnchors, plainKeys } from "../../shared/anchors";
import { HIDE_VIEWPORT_SCROLLBAR_CSS, LIVE_REPLAY_CSS } from "../../shared/replayStyle";
import type { Hotspot } from "../../shared/types";
import { anchorFor, anchorPx, pickScroller, scrollPos, scrollToTop, scrollWindowTo, windowScroll, type Box } from "./anchorDom";

/**
 * While editing a captured page, a hotspot tracks the actual DOM element it was drawn on rather
 * than a bare viewport coordinate. That's what lets you scroll to reach content further down the
 * page: while editing, the box follows its element as the page scrolls (see anchorShifts), so its
 * x/y/w/h always match what's on screen. On save the element is tagged with the hotspot's id and
 * the position it sat at (see serializeCapture and shared/anchors.ts), and the page's scroll state
 * is written into the HTML. The player then puts the box at "saved box + how far the element has
 * moved since it was saved" — it never reads the DOM from the parent (its frame is cross-origin
 * sandboxed); a small script of ours inside the frame measures and reports the movement.
 *
 * What an anchor is and how the page is measured (which element a box anchors to, where it sits,
 * which scroller a callout follows) is in ./anchorDom.ts, which that script is built from too: the
 * Studio and the player run the same code, so they can't place a hotspot or callout differently.
 */
export { ANCHOR_ATTR, anchorPx, pickScroller, scrollPos };

/** Class on <html> that shows hidden parts as ghosts while editing. Never saved. */
export const SHOW_HIDDEN_CLASS = "ih-show-hidden";
/** Marks <style> tags that only exist while editing. Never saved. */
export const EDITOR_STYLE_ATTR = "data-ih-editor";

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round = (v: number) => Math.round(v * 100) / 100;

/** A hotspot's box in the page's own CSS px, which is what the DOM is measured in. */
const boxPx = (h: Hotspot, imageWidth: number, imageHeight: number): Box => ({
  left: (h.x / 100) * imageWidth,
  top: (h.y / 100) * imageHeight,
  right: ((h.x + h.w) / 100) * imageWidth,
  bottom: ((h.y + h.h) / 100) * imageHeight,
});

/**
 * Is the box still sitting on `el`? An anchor is chosen by the box's centre, so that centre being
 * inside the element is exactly what "still tracking it" means — the box's own edges are drawn by
 * hand and line up with nothing. A tiny slack keeps a sub-pixel layout difference from reading as
 * "the box left its element" and re-anchoring it every frame.
 */
const holds = (el: Element, x: number, y: number) => {
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return false; // display:none or detached: it tracks nothing
  return x >= r.left - 1 && x <= r.right + 1 && y >= r.top - 1 && y <= r.bottom + 1;
};

// Several hotspots can sit on one element, so the attribute holds a space-separated list of keys.
const keysOn = (el: Element) => plainKeys(el.getAttribute(ANCHOR_ATTR));
const anchorEl = (d: Document, key: string) => d.querySelector(`[${ANCHOR_ATTR}~="${key}"]`);
const addKey = (el: Element, key: string) => {
  const keys = keysOn(el);
  if (!keys.includes(key)) el.setAttribute(ANCHOR_ATTR, [...keys, key].join(" "));
};
const dropKey = (el: Element, key: string) => {
  const keys = keysOn(el).filter((k) => k !== key);
  if (keys.length) el.setAttribute(ANCHOR_ATTR, keys.join(" ")); else el.removeAttribute(ANCHOR_ATTR);
};

/**
 * Keep every hotspot's anchor element in sync with where its box currently is: a box the user
 * dragged or resized off its element is re-tagged to whatever is now under its centre, and so is
 * any hotspot that isn't anchored yet. Cheap to call on every hotspot change — safe to run
 * mid-drag, and a no-op for a box that only moved because its page scrolled under it.
 *
 * Re-anchoring is deliberately conservative, because a hotspot with no anchor stops following its
 * content for the rest of the session: the tag it has is only given up once a replacement is in
 * hand, and a box whose centre has scrolled off the frame keeps the anchor it has rather than be
 * re-picked against a point the page cannot be hit-tested at.
 */
export function syncAnchors(d: Document, hs: Hotspot[], imageWidth: number, imageHeight: number) {
  for (const h of hs) {
    const box: Box = boxPx(h, imageWidth, imageHeight);
    const cx = (box.left + box.right) / 2;
    const cy = (box.top + box.bottom) / 2;
    // A centre off the frame can't be hit-tested — elementsFromPoint has nothing to return
    // there — so the hotspot keeps whatever anchor it has and is looked at again once the page
    // has scrolled back to it.
    if (cx < 0 || cy < 0 || cx > imageWidth || cy > imageHeight) continue;
    const existing = anchorEl(d, h.id);
    // <body> holds every point on the page, so it can never say the box left its content: one
    // anchored to bare background keeps looking for something better to attach to.
    const weak = existing === d.body || existing === d.documentElement;
    if (existing && !weak && holds(existing, cx, cy)) continue; // still tracking the box
    const el = anchorFor(d, box); // the player anchors a box it has no saved anchor for the same way
    if (!el) continue; // nothing to attach to: better the anchor we have (if any) than none
    if (existing && existing !== el) dropKey(existing, h.id);
    addKey(el, h.id);
  }
}

/** Last seen on-screen position (in % of the frame) of a hotspot's anchor element. */
export type AnchorTrack = { el: Element; x: number; y: number };

const anchorPos = (d: Document, el: Element, imageWidth: number, imageHeight: number) => {
  const p = anchorPx(d, el);
  return p && { x: (p[0] / imageWidth) * 100, y: (p[1] / imageHeight) * 100 };
};

/**
 * Read the anchors a saved page carries. Each tag is rewritten to plain keys (that is what the
 * rest of the editor selects on), and the position it was saved at is returned, as a baseline for
 * anchorShifts: the first poll then moves each box by however far its element now sits from where
 * it was saved — the same rule the player applies, so a page that lays out differently here than
 * where it was saved (another platform's fonts, say) shows the boxes where a viewer will see them.
 */
export function loadAnchors(d: Document, imageWidth: number, imageHeight: number) {
  const saved = new Map<string, AnchorTrack>();
  d.querySelectorAll(`[${ANCHOR_ATTR}]`).forEach((el) => {
    const tokens = parseAnchors(el.getAttribute(ANCHOR_ATTR));
    el.setAttribute(ANCHOR_ATTR, tokens.map((t) => t.key).join(" "));
    for (const t of tokens) {
      if (t.at) saved.set(t.key, { el, x: (t.at[0] / imageWidth) * 100, y: (t.at[1] / imageHeight) * 100 });
    }
  });
  return saved;
}

/**
 * Open a captured page for editing the way the player opens it (src/capture/replayFrame.ts), so every
 * box starts where Preview shows it:
 *  1. the page at the scroll position it was saved with (the one its boxes are stored against);
 *  2. measured there: each hotspot's baseline (the saved one, else where its element is now) and, for a
 *     hotspot the page has no anchor for yet, the element under it (as the player anchors it);
 *  3. then the main scroller and the page go to the top, which the next `anchorShifts` moves every box by.
 * Measuring after step 3 instead would put every box without a saved position — older captures, cloned
 * screens — over different content than Preview does, by however far the page was saved scrolled.
 *
 * `opened` is how far the main scroller was scrolled at step 1, in page px: what an older "Moves with Page"
 * callout is measured from (the player reports the same value as initPageX / initPageY).
 */
export function openCapture(d: Document, hs: Hotspot[], imageWidth: number, imageHeight: number) {
  normalizeScroll(d);
  const scroller = pickScroller(d);
  const opened = scrollPos(d, scroller);
  const track = loadAnchors(d, imageWidth, imageHeight);
  // Like the player: a hotspot keeps the element its token is on, and only one with no token is anchored here.
  syncAnchors(d, hs.filter((h) => !anchorEl(d, h.id)), imageWidth, imageHeight);
  for (const h of hs) {
    const el = !track.has(h.id) && anchorEl(d, h.id);
    const p = el && anchorPos(d, el, imageWidth, imageHeight);
    if (el && p) track.set(h.id, { el, ...p });
  }
  scrollToTop(d, scroller);
  return { scroller, opened, track };
}

/**
 * How far each anchored hotspot's element has moved on screen since the last call, in % of the
 * frame — what lets a box follow the page as it scrolls in the editor, instead of staying put
 * over whatever slides underneath it. `track` holds each anchor's last seen position and is
 * updated here; a hotspot whose anchor element is new (or gone) just gets a fresh baseline.
 */
export function anchorShifts(d: Document, hs: Hotspot[], track: Map<string, AnchorTrack>, imageWidth: number, imageHeight: number) {
  const shifts = new Map<string, { dx: number; dy: number }>();
  for (const h of hs) {
    const el = anchorEl(d, h.id);
    const p = el && anchorPos(d, el, imageWidth, imageHeight);
    if (!el || !p) { track.delete(h.id); continue; }
    const t = track.get(h.id);
    if (!t || t.el !== el) { track.set(h.id, { el, ...p }); continue; }
    if (p.x !== t.x || p.y !== t.y) {
      shifts.set(h.id, { dx: p.x - t.x, dy: p.y - t.y });
      t.x = p.x;
      t.y = p.y;
    }
  }
  return shifts;
}

/** The hotspot ids the page currently carries an anchor tag for. */
export function anchoredIds(d: Document): Set<string> {
  const out = new Set<string>();
  d.querySelectorAll(`[${ANCHOR_ATTR}]`).forEach((el) => {
    for (const k of plainKeys(el.getAttribute(ANCHOR_ATTR))) out.add(k);
  });
  return out;
}

/**
 * The part of a hotspot that is inside the frame. Only for a hotspot nothing will ever move: an
 * ANCHORED box is stored against the scroll position it was saved at and put back at "saved box +
 * how far its element has moved since" (shared/anchors.ts), so one sitting off the frame at that
 * scroll is correct and comes back as the viewer scrolls to it — clamping it instead would flatten
 * it to a 1%-tall sliver pinned to the edge of the viewport, which no later scroll undoes.
 */
export function clipToFrame(h: Hotspot): Hotspot {
  const x0 = clamp(h.x, 0, 99);
  const y0 = clamp(h.y, 0, 99);
  const x1 = clamp(h.x + h.w, x0 + 1, 100);
  const y1 = clamp(h.y + h.h, y0 + 1, 100);
  return { ...h, x: round(x0), y: round(y0), w: round(x1 - x0), h: round(y1 - y0) };
}

/**
 * The hotspots as they should be stored for a captured page, given the page they were drawn on.
 * An anchored box is kept exactly as it is, off the frame included (up to MAX_OFF_FRAME_PCT, which
 * the server accepts for a captured page): it is stored against the scroll position saved with it
 * and put back at "saved box + how far its element has moved since", so one the page is scrolled
 * past right now comes back into place as the viewer scrolls to it.
 * A hotspot the page carries no anchor for is clipped, because nothing will ever move it.
 */
export function clipUnanchored(d: Document | null, hs: Hotspot[]): Hotspot[] {
  const anchored = d && anchoredIds(d);
  const offFrame = (v: number) => clamp(v, -MAX_OFF_FRAME_PCT, MAX_OFF_FRAME_PCT); // as far off the frame as the server accepts
  return hs.map((h) => (anchored && anchored.has(h.id) ? { ...h, x: offFrame(h.x), y: offFrame(h.y) } : clipToFrame(h)));
}

// A capture is stored frozen: replay without scripts can't scroll, so the page's scroll position
// is expressed as a negative margin on <html>, and each inner scroller's as a negative-margin
// spacer inside it (the extension writes both). The player swaps the root one for real scrolling.
const frozenCss = (sx: number, sy: number) =>
  "html,body{overflow:hidden!important}html{scroll-behavior:auto!important}" +
  (sy || sx ? `html{margin-top:${-sy}px!important;margin-left:${-sx}px!important}` : "") +
  "a[data-ih-href]{cursor:pointer}";

/**
 * Turn a capture's frozen scroll state into real scroll positions, once on load, and treat those
 * as the only source of truth for the rest of the editing session. Otherwise scrolling in the
 * editor would stack on top of the frozen trick: the page would jump on save, and an inner
 * scroller moved after a hotspot was drawn over it would be saved at its old position, leaving
 * the box over different content. serializeCapture writes the real positions back.
 * The frozen style is swapped for the one the player's scrollable replay uses (LIVE_REPLAY_CSS), so
 * the page lays out and scrolls the same here as there: a sticky header sticks in both.
 */
export function normalizeScroll(d: Document) {
  d.querySelectorAll<HTMLElement>("[data-ih-scroll]").forEach((el) => {
    const [top, left] = (el.getAttribute("data-ih-scroll") ?? "").split(",").map(parseFloat);
    Array.from(el.children).forEach((c) => { if (c.hasAttribute("data-ih-spacer")) c.remove(); });
    el.removeAttribute("data-ih-scroll");
    el.scrollTo({ top: top || 0, left: left || 0, behavior: "instant" }); // a page's own scroll-behavior:smooth mustn't animate this
  });
  const tag = d.querySelector("style[data-ih-replay]") as HTMLStyleElement | null;
  const css = tag?.textContent ?? "";
  const my = /margin-top:\s*(-?[\d.]+)px/.exec(css);
  const mx = /margin-left:\s*(-?[\d.]+)px/.exec(css);
  // Drop the margin trick and the frozen overflow; serializeCapture rebuilds both from the real scroll position.
  if (tag) tag.textContent = LIVE_REPLAY_CSS + HIDE_VIEWPORT_SCROLLBAR_CSS;
  if (!my && !mx) return;
  // As the player restores it (a page that scrolls on <body> is scrolled there).
  scrollWindowTo(d, mx ? -parseFloat(mx[1]) : 0, my ? -parseFloat(my[1]) : 0);
}

/**
 * The page as it should be stored, built from a copy: the live page the editor is showing is never
 * touched, so a failed save leaves nothing to repair. On the copy this
 *  - drops what only exists while editing (editor styles, the "show hidden" class),
 *  - writes the page's scroll position and every inner scroller's back as the frozen trick,
 *  - tags each hotspot's element with `id@x,y`: its key and where it sits right now, which is
 *    where the box (saved in the same request) is positioned against — and drops tags for hotspots
 *    that no longer exist.
 */
export function serializeCapture(d: Document, hotspots: Pick<Hotspot, "id">[]): string {
  const root = d.documentElement;
  const clone = root.cloneNode(true) as HTMLElement;
  const live = [root, ...Array.from(root.querySelectorAll("*"))];
  const copy = [clone, ...Array.from(clone.querySelectorAll("*"))]; // same order: element i of one is element i of the other
  const wanted = new Set(hotspots.map((h) => h.id));

  live.forEach((L, i) => {
    const C = copy[i];
    if (!C) return;
    const scrolled = L as HTMLElement;
    if (L !== root && L !== d.body && !/^(TEXTAREA|INPUT|SELECT)$/.test(L.tagName) && (scrolled.scrollTop || scrolled.scrollLeft)) {
      C.setAttribute("data-ih-scroll", `${scrolled.scrollTop},${scrolled.scrollLeft}`);
      const spacer = d.createElement("div");
      spacer.setAttribute("style", `margin-top:${-scrolled.scrollTop}px;margin-left:${-scrolled.scrollLeft}px;height:0;width:0;flex:none`);
      spacer.setAttribute("data-ih-spacer", "");
      C.insertBefore(spacer, C.firstChild);
    }
    if (L.hasAttribute(ANCHOR_ATTR)) {
      const at = anchorPx(d, L);
      const tokens = parseAnchors(L.getAttribute(ANCHOR_ATTR)).filter((t) => wanted.has(t.key)).map((t) => ({ key: t.key, at }));
      if (tokens.length) C.setAttribute(ANCHOR_ATTR, formatAnchors(tokens)); else C.removeAttribute(ANCHOR_ATTR);
    }
  });

  clone.querySelectorAll(`style[${EDITOR_STYLE_ATTR}]`).forEach((n) => n.remove());
  clone.classList.remove(SHOW_HIDDEN_CLASS);
  if (!clone.getAttribute("class")) clone.removeAttribute("class");

  const [sx, sy] = windowScroll(d);
  let tag = clone.querySelector("style[data-ih-replay]") as HTMLStyleElement | null;
  if (!tag) {
    tag = d.createElement("style");
    tag.setAttribute("data-ih-replay", "");
    (clone.querySelector("head") ?? clone).appendChild(tag);
  }
  tag.textContent = frozenCss(sx, sy);

  return `<!DOCTYPE html>\n${clone.outerHTML}`;
}
